import React, { useMemo, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Search, ListX, Sparkles, Check, Layers, X } from 'lucide-react';

type VirtualizedListProps<T> = {
  items: T[];
  getKey: (item: T) => string | number;
  renderItem: (item: T) => React.ReactNode;
  getLabel: (item: T) => string;
  // NEW: Function to determine which group an item belongs to
  getCategory?: (item: T) => string;
  rowHeight: number;

  height?: string;
  className?: string;

  isSelectable?: boolean;
  selectionMode?: 'single' | 'multiple';
  selectedIds?: (string | number)[];
  onSelectionChange?: (id: string | number) => void;

  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modalTitle?: string;
};

type VirtualItemWrapper<T> =
  | { type: 'header'; title: string }
  | { type: 'item'; data: T };

const ListContent = <T extends unknown>({
  items,
  getKey,
  renderItem,
  getLabel,
  getCategory,
  height = '400px',
  rowHeight,
  isSelectable,
  selectedIds = [],
  onSelectionChange,
  onCloseModal,
}: VirtualizedListProps<T> & { onCloseModal?: () => void }) => {
  const [parentRef, setParentRef] = useState<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');

  const filteredAndGroupedItems = useMemo(() => {
    let processed = items;

    if (search) {
      const lowerSearch = search.toLowerCase();
      processed = items.filter((item: any) => {
        // Safe check for label
        const label = getLabel(item)?.toLowerCase() ?? '';
        if (label.includes(lowerSearch)) return true;

        if (item?.properties && typeof item.properties === 'object') {
          return Object.values(item.properties).some(
            (v) =>
              typeof v === 'string' && v.toLowerCase().includes(lowerSearch),
          );
        }

        const structureNo = item?.asset?.structureNo?.toLowerCase();
        const address = item?.address?.toLowerCase();
        const rawStructure = item?.structureNo?.toLowerCase();

        return (
          structureNo?.includes(lowerSearch) ||
          address?.includes(lowerSearch) ||
          rawStructure?.includes(lowerSearch)
        );
      });
    }

    if (!getCategory || processed.length === 0) {
      return processed.map((item) => ({ type: 'item' as const, data: item }));
    }

    // Sort items by category
    const sorted = [...processed].sort((a, b) => {
      const catA = getCategory(a);
      const catB = getCategory(b);
      return catA.localeCompare(catB);
    });

    const results: VirtualItemWrapper<T>[] = [];
    let lastCategory = '';

    sorted.forEach((item) => {
      const category = getCategory(item);
      // If we hit a new category, push a Header object
      if (category !== lastCategory) {
        results.push({ type: 'header', title: category });
        lastCategory = category;
      }
      // Push the actual item
      results.push({ type: 'item', data: item });
    });

    return results;
  }, [items, search, getLabel, getCategory]);

  const visibleItemCount = filteredAndGroupedItems.filter(
    (i) => i.type === 'item',
  ).length;

  // --- Virtualizer ---
  const rowVirtualizer = useVirtualizer({
    count: filteredAndGroupedItems.length,
    getScrollElement: () => parentRef,
    estimateSize: (index) => {
      // Dynamic height: Headers are shorter (32px), Items are standard (rowHeight)
      return filteredAndGroupedItems[index].type === 'header' ? 32 : rowHeight;
    },
    overscan: 5,
    enabled: !!parentRef,
  });

  const totalSize = rowVirtualizer.getTotalSize();
  const isSearchEmpty =
    items.length > 0 &&
    filteredAndGroupedItems.length === 0 &&
    search.length > 0;
  const isListEmpty = items.length === 0;

  const handleItemClick = (wrapper: VirtualItemWrapper<T>) => {
    // Ignore clicks on headers
    if (wrapper.type !== 'item') return;

    if (!isSelectable || !onSelectionChange) return;

    const id = getKey(wrapper.data);
    onSelectionChange(id);

    if (onCloseModal) {
      onCloseModal();
    }
  };

  //  --- Empty States ---
  let emptyStateContent = null;

  if (isListEmpty) {
    emptyStateContent = (
      <div className="flex flex-col items-center justify-center h-full text-center px-6 py-12">
        <div className="rounded-full bg-slate-100 p-4 mb-4">
          <ListX className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-sm font-semibold text-slate-900 mb-1">
          No items yet
        </h3>
        <p className="text-sm text-slate-500 max-w-xs">Your list is empty.</p>
      </div>
    );
  } else if (isSearchEmpty) {
    emptyStateContent = (
      <div className="flex flex-col items-center justify-center h-full text-center px-6 py-12">
        <div className="rounded-full bg-secondary p-4 mb-4">
          <Search className="h-8 w-8 text-secondary-500" />
        </div>
        <h3 className="text-sm font-semibold text-foreground mb-1">
          No results found
        </h3>
        <p className="text-sm text-foreground max-w-xs mb-3">
          We couldn't find any matches for{' '}
          <span className="font-medium text-muted-foreground">"{search}"</span>
        </p>
        <button
          onClick={() => setSearch('')}
          className="cursor-pointer text-xs font-medium text-secondary hover:text-secondary/80 transition-colors"
        >
          Clear search
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 w-full h-full flex flex-col">
      {/* Search Input */}
      <div className="relative group shrink-0">
        <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground" />
        <Input
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 pr-4 h-11"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="cursor-pointer absolute right-3 top-1/2 transform -translate-y-1/2 text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {/* List Container */}
      <div
        ref={setParentRef}
        style={{ height: height }}
        className={cn(
          'overflow-y-auto rounded-lg border border-border bg-background transition-all w-full relative',
          (isListEmpty || isSearchEmpty) && 'border-border bg-background/30',
        )}
      >
        {emptyStateContent ? (
          <div className="h-full w-full">{emptyStateContent}</div>
        ) : (
          <div
            style={{
              height: totalSize,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const wrapper = filteredAndGroupedItems[virtualItem.index];

              // --- Render HEADER ---
              if (wrapper.type === 'header') {
                return (
                  <div
                    key={virtualItem.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: virtualItem.size,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                    className="bg-slate-100 px-4 flex items-center text-xs font-semibold text-primary uppercase tracking-wider sticky z-10"
                  >
                    <Layers className="w-3 h-3 mr-2" />
                    {wrapper.title}
                  </div>
                );
              }

              // --- Render ITEM ---
              const item = wrapper.data;
              const id = getKey(item);
              const isSelected = selectedIds.includes(id);

              return (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={rowVirtualizer.measureElement}
                  onClick={() => handleItemClick(wrapper)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: virtualItem.size,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                  className={cn(
                    'flex items-center w-full px-4 border-b border-border last:border-b-0',
                    isSelectable
                      ? 'cursor-pointer hover:bg-slate-50 active:bg-slate-100'
                      : 'cursor-default',
                    isSelected && 'bg-primary/5 hover:bg-primary/10',
                  )}
                >
                  <div className="flex-1 min-h-0 min-w-0 py-3 flex items-center justify-between">
                    {renderItem(item)}
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary ml-2 shrink-0" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Footer */}
      {!isListEmpty && !isSearchEmpty && (
        <div className="flex items-center justify-between px-1 shrink-0 pt-1">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Sparkles className="h-3.5 w-3.5 text-slate-400" />
            <span>
              Showing{' '}
              <span className="font-medium text-slate-700">
                {visibleItemCount}
              </span>
              {visibleItemCount !== items.length && (
                <>
                  {' '}
                  of{' '}
                  <span className="font-medium text-slate-700">
                    {items.length}
                  </span>
                </>
              )}{' '}
              item{items.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const VirtualizedList = <T extends unknown>(
  props: VirtualizedListProps<T>,
) => {
  const { open, onOpenChange, modalTitle, ...listProps } = props;
  if (open !== undefined && onOpenChange !== undefined) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg w-full p-6 gap-4">
          <DialogHeader className="pb-2">
            <DialogTitle>{modalTitle || 'Select Item'}</DialogTitle>
          </DialogHeader>
          <div className="w-full">
            <ListContent
              {...listProps}
              height={listProps.height || '400px'}
              onCloseModal={() => onOpenChange(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  return <ListContent {...listProps} />;
};
